import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { fabric } from 'fabric';
import { ToastVariants } from '@dotcom/crown-ui';
import warningIcon from '../../../assets/warning-icon.svg';
import { addIconToObject, IconsTypes } from '../../../components/card-editor/utils/add-icon-to-object';
import { removeEditableAreaButtons } from '../../../components/card-editor/utils/remove-editable-area-buttons';
import { useAnalyticsContext } from '../../../context/analytics-context';
import { setIsToasterOpen, useAppContext } from '../../../context/app-context';
import { setSelectedPhotoZone, useCardContext } from '../../../context/card-context';
import { useActiveCanvas } from '../../../hooks/use-active-canvas';
import { useDatadog } from '../../../hooks/use-datadog';
import { useFeatureFlags } from '../../../hooks/use-feature-flags';
import { isSplitPanelFace } from '../../../utils/panel-zoom';
import { setPhotoZoneScaleRatio, hideMiddleControls, hideAllControls } from '../../../utils/canvas';
import { addImageToCanvas } from '../../../utils/canvas-utils/add-image-to-canvas';
import { addCardFaceImageMoveConstraint } from '../../../utils/canvas-utils/add-card-face-image-move-constraint';
import { CanvasDataTypes } from '../../../utils/canvas-utils/canvas-data-types';
import { getCardFaceClipPath } from '../../../utils/canvas-utils/get-card-face-clip-path';
import { createClipPathRect } from '../../../utils/canvas-utils/create-clip-path-rect';
import { bringEditableAreaBordersToFront } from '../../../utils/canvas-utils/bring-editable-area-borders-to-front';
import { createPhotoFromImage, setImageOnSelect } from '../utils/add-photo-utils';
import { addPhotozoneImageMoveConstraint } from '../utils/add-photozone-image-move-constraint';
import { getNextCoverPhotoZone } from '../utils/get-next-cover-photozone';
import { clearPhotoZoneHighlight } from '../../../utils/canvas-utils/highlight-photo-zone';
import { removeExistingPhotoZoneImage } from '../../../utils/canvas-utils/remove-existing-photo-zone-image';
import { useOpenImageEditDrawer } from './use-open-image-edit-drawer';
import type { FillSelectedPhotoZoneParams } from './use-photo-types';
import { useAddLowResIcon } from '../../../hooks/use-add-low-res-icon';
import { getImageOrientation } from '../../../components/card-controls/rotate-drawer/rotate-drawer-utils';
import { useHistoryTransaction } from '../../edit-history/hooks/use-history-transaction';

// The factor in which we want to scale the image when needs
// to be resized for it to be bigger than the photoZone.
// In this case is 1.1 because I want the image to be 10% bigger
// than the photoZone.
export const SCALE_IMAGE_FACTOR = 1.1;

/**
 * Custom hook to manage photo-related functionalities in the card editor.
 * It provides methods to scale photos, select photos, and add photos to the canvas.
 */
export const usePhoto = () => {
  const { appDispatch } = useAppContext();
  const datadog = useDatadog();
  const { t } = useTranslation();
  const { firstElementSelected, updateEditFormats, isReplacingImage } = useAnalyticsContext();
  const { cardState, cardDispatch } = useCardContext();
  const { selectedPhotoZone, cardFacesList, activeCardIndex } = cardState;
  const canvas = useActiveCanvas();
  const currentCanvas = canvas?.current;
  const currentCardFace = cardFacesList[`${activeCardIndex}`];
  const canvasObjects = currentCanvas?.getObjects() ?? [];
  const { IS_FORCE_FILL_ENABLED } = useFeatureFlags();
  const handleIconAdded = useAddLowResIcon();
  const openImageEditDrawer = useOpenImageEditDrawer();
  const runHistoryTransaction = useHistoryTransaction();

  const recordPlacement = (placement: () => void): Promise<void> => {
    const targetCanvas = canvas?.current;
    if (!targetCanvas) {
      placement();
      return Promise.resolve();
    }
    return runHistoryTransaction(targetCanvas, placement);
  };

  /**
   * Fills a given photo-zone using the id. This will update the canvas with the given image
   * @param params Object containing image, photozoneId, and onIconAdded callback
   */
  const fillPhotoZone = ({ image, photozoneId, onIconAdded }: FillSelectedPhotoZoneParams): void => {
    const currentPhotozoneObject = canvasObjects.find(
      (object) => object.name === (photozoneId ?? selectedPhotoZone?.id),
    );
    const shouldHideAllHandles = currentCardFace.type === 'front' && currentCardFace.active === true;
    if (currentPhotozoneObject) {
      const clipPath = new fabric.Rect({
        width: currentPhotozoneObject.width,
        height: currentPhotozoneObject.height,
        left: currentPhotozoneObject.left,
        angle: currentPhotozoneObject.angle,
        top: currentPhotozoneObject.top,
        absolutePositioned: true,
      });
      const imageDimensions = {
        left: currentPhotozoneObject.left ?? 0,
        top: currentPhotozoneObject.top ?? 0,
      };
      if (image.data.positionOverrides) {
        imageDimensions.left = image.data.positionOverrides.left;
        imageDimensions.top = image.data.positionOverrides.top;
      }
      const coverScale =
        setPhotoZoneScaleRatio(
          image.width as number,
          image.height as number,
          currentPhotozoneObject.width as number,
          currentPhotozoneObject.height as number,
        ) * SCALE_IMAGE_FACTOR;

      const originalOrientation = getImageOrientation(image);
      const originalScale = coverScale;

      image.set({
        ...imageDimensions,
        clipPath,
        data: {
          ...image.data,
          photoZoneId: currentPhotozoneObject?.name,
          type: CanvasDataTypes.PhotoZoneImage,
          originalOrientation,
          originalScale,
        },
        angle: currentPhotozoneObject.angle,
        scaleX: coverScale,
        scaleY: coverScale,
        onSelect: () => {
          return false;
        },
      });
      hideMiddleControls(image);
      if (IS_FORCE_FILL_ENABLED && shouldHideAllHandles) {
        addPhotozoneImageMoveConstraint(image, currentPhotozoneObject);
        hideAllControls(image);
      } else {
        hideMiddleControls(image);
      }
      currentCanvas?.add(image);
      image.sendToBack();
      if (currentCanvas) {
        void addIconToObject(currentCanvas as fabric.Canvas, image, warningIcon, IconsTypes.LowRes, {
          iconOffset: 35,
          isLowResIcon: true,
          onIconAdded,
          isStaticIcon: true,
          photoZone: currentPhotozoneObject,
        }).catch(() => {
          setIsToasterOpen(appDispatch, {
            title: t('generalError.title'),
            children: t('generalError.message'),
            variant: ToastVariants.Error,
          });
        });
      }
      currentPhotozoneObject.set({ visible: false, data: { ...currentPhotozoneObject.data } });
      currentPhotozoneObject.data.hasContent = true;
      canvasObjects.forEach((object) => {
        if (object.name === `${currentPhotozoneObject?.name}-button`) {
          object.set({ visible: false });
        }
        if (object.name === CanvasDataTypes.PhotoFrameName) {
          object.bringToFront();
        }
      });
      currentCanvas?.setActiveObject(image);
    }
  };

  /**
   * Add a photo to a photo zone (front face logic)
   * @param {fabric.Image} image - The image to be added to the photo zone
   * @returns {Promise<void>} - Resolves once the placement is recorded in edit history
   */
  const addPhotoToPhotozone = useCallback(
    (image: fabric.Image): Promise<void> => {
      setImageOnSelect(image, openImageEditDrawer, isReplacingImage, updateEditFormats, firstElementSelected);
      if (image?.width === 0 || image?.height === 0) {
        throw new Error('The height and width of the image being attempted to upload is 0 (zero).');
      }

      const photo = createPhotoFromImage(image);

      datadog.addAction('add-photo-to-user-zone', {
        image: photo,
        zoneId: selectedPhotoZone.id,
        cardFace: currentCardFace?.type ?? null,
      });
      return recordPlacement(() => {
        if (selectedPhotoZone.id) {
          // PRCM-10916: the user picked a specific (highlighted) zone, so replace
          // in place rather than filling the next empty zone. Remove any existing
          // image already occupying this zone (and its low-res PhotoIcon) before
          // dropping the new one in. Shared with the drag-to-canvas drop flow.
          removeExistingPhotoZoneImage(currentCanvas, selectedPhotoZone.id);
          fillPhotoZone({ image, onIconAdded: handleIconAdded });
        } else {
          const nextCoverSlot = getNextCoverPhotoZone(canvas?.current as fabric.Canvas);
          if (nextCoverSlot) {
            fillPhotoZone({
              image,
              onIconAdded: handleIconAdded,
              photozoneId: nextCoverSlot.name,
            });
          }
        }
        clearPhotoZoneHighlight(currentCanvas);
      }).then(() => {
        setTimeout(() => openImageEditDrawer(), 0);
      });
    },
    [
      appDispatch,
      t,
      cardDispatch,
      isReplacingImage,
      updateEditFormats,
      firstElementSelected,
      canvas,
      currentCanvas,
      currentCardFace,
      canvasObjects,
      IS_FORCE_FILL_ENABLED,
      selectedPhotoZone,
      openImageEditDrawer,
      runHistoryTransaction,
    ],
  );

  /**
   * Add a photo directly to the card face (non-front face logic)
   * @param {fabric.Image} image - The image to be added to the card face
   * @returns {Promise<void>} - Resolves once the placement is recorded in edit history
   */
  const addPhotoToCardFace = useCallback(
    (image: fabric.Image, options?: { preservePosition?: boolean }): Promise<void> => {
      setImageOnSelect(image, openImageEditDrawer, isReplacingImage, updateEditFormats, firstElementSelected);
      if (image?.width === 0 || image?.height === 0) {
        throw new Error('The height and width of the image being attempted to upload is 0 (zero).');
      }

      const photo = createPhotoFromImage(image);

      datadog.addAction('add-photo-to-card-face', {
        image: photo,
        zoneId: selectedPhotoZone.id,
        cardFace: currentCardFace?.type ?? null,
      });
      const cardFaceClipPath = getCardFaceClipPath(currentCardFace, 0);
      const imageHalfSize = 100;
      const rightPanelImageOffset = 100;
      if (options?.preservePosition) {
        if (cardFaceClipPath) {
          image.set({ clipPath: createClipPathRect(cardFaceClipPath) });
        }
      } else if (cardFaceClipPath) {
        const freshClipPath = createClipPathRect(cardFaceClipPath);
        image.set({
          top: cardFaceClipPath.top + (cardFaceClipPath.height as number) / 2 - imageHalfSize,
          left: cardFaceClipPath.left + (cardFaceClipPath.width as number) / 2 - imageHalfSize,
          clipPath: freshClipPath,
        });
      } else if (isSplitPanelFace(currentCardFace?.type) && (canvas?.current?.viewportTransform?.[4] ?? 0) < 0) {
        // Right-panel zoom is active — offset image into the right panel
        const canvasZoom = canvas?.current?.getZoom() ?? 1;
        const halfWidth = (canvas?.current?.getWidth() ?? 0) / canvasZoom;
        image.set({ left: halfWidth + rightPanelImageOffset });
      }
      return recordPlacement(() => {
        addImageToCanvas(image, canvas?.current as fabric.Canvas, handleIconAdded);
        // PRCM-11096: constrain freely-placed images to the card face bounds so
        // dragging/repositioning never exposes empty space past the card edge.
        // Applied to every free image (click- or drag-placed) for consistent
        // behavior regardless of how it was added.
        const faceCanvas = canvas?.current;
        if (faceCanvas) {
          const zoom = faceCanvas.getZoom() || 1;
          // getBoundingRect(absolute) works in OBJECT space, so bounds must be
          // expressed there too. For split-panel Inside faces the visible panel
          // is panned via the viewport transform (inside-right translates left by
          // halfWidth), so offset the bounds by -translate/zoom to track whichever
          // panel is on screen instead of always clamping to the left panel.
          const viewport = faceCanvas.viewportTransform ?? [1, 0, 0, 1, 0, 0];
          addCardFaceImageMoveConstraint(image, {
            left: -(viewport[4] ?? 0) / zoom || 0,
            top: -(viewport[5] ?? 0) / zoom || 0,
            width: faceCanvas.getWidth() / zoom,
            height: faceCanvas.getHeight() / zoom,
          });
        }
        bringEditableAreaBordersToFront(canvas?.current);
        removeEditableAreaButtons(canvas?.current as fabric.Canvas);
      }).then(() => {
        setSelectedPhotoZone(cardDispatch, null, null);
        setTimeout(() => openImageEditDrawer(), 0);
      });
    },
    [
      appDispatch,
      t,
      cardDispatch,
      isReplacingImage,
      updateEditFormats,
      firstElementSelected,
      canvas,
      openImageEditDrawer,
      runHistoryTransaction,
    ],
  );

  return { addPhotoToPhotozone, addPhotoToCardFace, fillPhotoZone };
};
